import Page from '../../../components/PageWrapper';
import clsx from 'clsx';
import { useState, useMemo, useEffect } from 'react';
import GTable from '../../../components/elements/GTable';
import { fetchAllLogs, getAllAdminAccounts } from '../../../api/requests';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import Avatar from '../../../components/Avatar';
import { getFormattedAccessRight, getNameInitials, onRemoveFilter, updateQueriesList } from '../../../utils/miscFunctions';
import { ITablePaginationData, OtherActionsElement } from '../../../components/elements/GTable/GTable';
import ViewLog from './ViewLog';
import TableFilter from '../../../components/elements/GTable/InGTableComponents/TableFilter';
import TableFilterForm from '../../../components/elements/GTable/InGTableComponents/TableFilter/TableFilterForm';
import { ITableFilterItem } from '../../../resources/interfaces';
import { useTableFilters } from '../../../hooks/useTableFilters';
import { useSearchParams } from 'react-router-dom';
import { COMPANY_TYPES } from '../../../resources/enums';
import useUserAccount from '../../../hooks/userAccountHook';
import { FileUp } from 'lucide-react';
import useAuditLogs from '../../../hooks/auditLogsHook';

interface AuditLogsProps {
  isSettings?: boolean;
}

const AuditLogs = ({ isSettings = false }: AuditLogsProps = {}) => {
  const [showLogDetails, setShowLogDetails] = useState<boolean>(false);
  const [log, setLog] = useState<any>();
  const { user } = useUserAccount();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPerPage, setShowPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(searchParams.get('page') ? Number(searchParams.get('page')) : 0);
  const [auditLogsPaginationData, setAuditLogsPaginationData] = useState<ITablePaginationData>();
  const [searchString, setSearchString] = useState<string>();
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [admins, setAdmins] = useState<any[]>([]);
  const { AuditLogsHookModal, showAuditLogsModal, } = useAuditLogs();
  const {
    filters: tableFilters,
    setFilters,
    getFilterValue,
  } = useTableFilters({
    initialFilters: [],
    syncWithUrl: true,
  });

  function getDefaultFilters() {
    const filters: ITableFilterItem[] = [
      {
        name: 'manager',
        value: searchParams.get('manager') ? String(searchParams.get('manager')) : '',
        value2: searchParams.get('manager') ? String(searchParams.get('manager')) : '',
        type: 'select',
        list: admins,
        placeholder: `Select policy manager`,
        label: `Policy manager`
      }
    ];
    return filters;
  }

  const getOtherActions = () => {
    let actions = [];
    if (user.isInsurer() || user.isInternalAdmin() || user.isBroker()) {
      actions.push({
        label: 'Export audit logs',
        icon: <FileUp className="h-4 w-4" />,
        callback: () => showAuditLogsModal(admins)
      });
    }
    return actions;
  };

  useEffect(() => {
    setFilters(getDefaultFilters());
  }, [admins]);

  const searchParam = searchString;
  const managerId = searchParams.get('manager') ? searchParams.get('manager') : getFilterValue('manager');

  const { isLoading, data: auditLogsResponse } = useQuery({
    queryKey: ['getAuditLogs', currentPage, showPerPage, managerId, searchParam],
    queryFn: async () => {
      try {
        const response: any = await fetchAllLogs(currentPage, showPerPage, managerId, searchParam);
        if (response) {
          const from = response?.pageable?.offset + 1;
          const to =
            from + response?.size > response?.totalElements
              ? response?.totalElements
              : from + response?.size - 1;

          setAuditLogsPaginationData({
            ...auditLogsPaginationData,
            initialPage: currentPage,
            per_page: response?.size,
            total: response?.totalElements,
            totalPages: response?.totalPages,
            from: from,
            to: to
          });

          return response;
        }
      } catch (error) {
        console.error('Error fetching audit logs:', error);
        return null;
      }
    }
  });

  const getAllAdmins = () => {
    getAllAdminAccounts()
      .then((response: any) => {
        const managersList = user.isBroker() ? response?.filter((admin: any) => admin?.company?.type === COMPANY_TYPES.BROKER && admin?.company?.name === user.company?.name) : response;
        const _admins = managersList?.map((admin: any) => ({
          name: `${admin.firstName} ${admin.lastName} (${getFormattedAccessRight(
            admin.accessRight
          )})`,
          value: admin.id,
        }));
        setAdmins(_admins);
      })
      .catch(() => { });
  };

  useQuery({ queryKey: ['getAllAdmins'], queryFn: getAllAdmins });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const columns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'User',
            accessor: 'fullName',
            Cell: (row: any) => {
              return (
                <div className="flex items-center gap-3">
                  <Avatar
                    size="xs"
                    // src={row?.row?.original?.account?.profileImage}
                    nameInitials={getNameInitials(row?.cell?.value)}
                  />

                  <span className={clsx('')}>{row?.cell?.value}</span>
                </div>
              );
            }
          },
          {
            Header: 'Action',
            accessor: 'activity',
            Cell: (row: any) => {
              return (
                <span className={clsx('')}>
                  {row.cell.value ? getFirstFiveWords(row.cell.value) : '-'}
                </span>
              );
            }
          },
          {
            Header: 'IP Address',
            accessor: 'ipAddress',
            Cell: (row: any) => {
              return (
                <span className={clsx('')}>
                  {row.cell.value ? getFirstFiveWords(row.cell.value) : '-'}
                </span>
              );
            }
          },

          {
            Header: 'Time',
            accessor: 'createdOn',
            Cell: (row: any) => {
              return (
                <span className={clsx('')}>
                  {row.cell.value ? moment(row.cell.value).format('MMM Do YY, h:mm a') : '-'}
                </span>
              );
            }
          }
        ]
      }
    ],
    []
  );

  function getFirstFiveWords(text: string) {
    const words = text.split(' ');
    const truncatedWords = words.slice(0, 5).join(' ');

    return text.length > 5 ? `${truncatedWords}...` : text;
  }

  return (
    <Page title="Audit Logs">
      <div className="bg-white w-full p-5 h-fit rounded-lg shadow-md dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
        {!isSettings && (
          <div className="">
            <div className="inline-flex items-center gap-2 mb-2">
              <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
              <h2 className="font-semibold text-3xl dark:text-darkBg-mainText">Audit Logs</h2>
            </div>

            <p className="text-sm text-slate-400">
              View a list of all user logs and associated details.{' '}
            </p>
          </div>
        )}

        <div className="overflow-x-auto">
          <GTable
            columns={columns}
            data={auditLogsResponse?.content || []}
            tableType={'auditLogs'}
            onSearch={(searchString: string) => setSearchString(searchString)}
            columnClasses=""
            classes="h-fit"
            loading={isLoading}
            paginationData={auditLogsPaginationData}
            onPageChange={handlePageChange}
            showPerPage={showPerPage}
            onShowPerPageChange={(page: number) => {
              setShowPerPage(page);
              setCurrentPage(0);
            }}
            onRowClick={(log: any) => {
              setLog(log);
              setShowLogDetails(true);
            }}
            selectedFilters={tableFilters}
            onRemoveFilter={(filter: ITableFilterItem, index: number) => {
              onRemoveFilter(filter, index, tableFilters, setFilters, setSearchParams)
            }}
            showFilter={
              <TableFilter
                openFilter={openFilter}
                onFilterChange={(filterState: boolean) => setOpenFilter(filterState)}
                content={
                  <TableFilterForm
                    filters={tableFilters}
                    getValues={(filters: ITableFilterItem[]) => {
                      setFilters(filters);
                      setOpenFilter(false);
                    }}
                    onResetFilters={() => setFilters(getDefaultFilters())}
                  />
                }
              />
            }
            actionElement={
              getOtherActions().length > 0 && (
                <OtherActionsElement getOtherActions={getOtherActions} />
              )
            }
          />
        </div>

        {log && showLogDetails && (
          <ViewLog
            show={showLogDetails}
            onClose={() => {
              setShowLogDetails(false);
            }}
            log={log}
          />
        )}
      </div>
      <AuditLogsHookModal />
    </Page>
  );
};

export default AuditLogs;
